import React, { useState, useEffect } from 'react';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  IconButton,
  Typography,
  Box
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { postData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';

const BrandModal = ({
  open,
  onClose,
  onSave,
  brandData = null,
  mode = 'create',
  existingBrands = []
}) => {
  const { companyId, userId, token } = useAuth();
  const { logAudit } = useAuditLog();
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  const [formData, setFormData] = useState({
    Name: '',
  });

  const [errors, setErrors] = useState({});
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('');

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (open) {
      if (brandData && mode !== 'create') {
        setFormData({
          Id: brandData.Id || '',
          Name: brandData.Name || '',
        });
      } else {
        setFormData({
          Name: '',
        });
      }
      setErrors({});
    }
  }, [open, brandData, mode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.Name.trim()) {
      newErrors.Name = 'Brand name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkDuplicateBrand = () => {
    const duplicate = existingBrands.find(brand => {
      if (mode === 'edit' && brandData && brand.Id === brandData.Id) {
        return false;
      }

      const nameMatch = brand.Name?.trim().toLowerCase() === formData.Name.trim().toLowerCase();

      return nameMatch;
    });

    return duplicate;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const duplicate = checkDuplicateBrand();
    if (duplicate) {
      setDuplicateMessage(`A brand with the name "${formData.Name}" already exists.`);
      setDuplicateDialogOpen(true);
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        Name: formData.Name,
        CompanyId: companyId,
      };

      if (mode === 'edit') {
        if (!brandData || !brandData.Id) {
          console.error('Cannot update: Missing brand ID');
          alert('Error: Cannot update brand - missing ID');
          return;
        }
        dataToSend.Id = brandData.Id;
        dataToSend.ModifiedBy = userId;
        dataToSend.ModifiedDate = new Date();
        if (brandData.CreatedBy) dataToSend.CreatedBy = brandData.CreatedBy;
        if (brandData.CreatedDate) dataToSend.CreatedDate = brandData.CreatedDate;

        await updateData('BrandApi', brandData.Id, dataToSend, token);
      logAudit(AUDIT_ACTION.UPDATE, 'BrandApi', brandData.Id, { ...selectedItem }, { ...formData });
      } else {
        dataToSend.CreatedBy = userId;
        dataToSend.CreatedDate = new Date();

        await postData('BrandApi', dataToSend, token);
      logAudit(AUDIT_ACTION.CREATE, 'BrandApi', 0, null, { Name: formData.Name || '' });
      }

      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving brand:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (mode) {
      case 'create': return 'New Brand';
      case 'edit': return 'Edit Brand';
      case 'view': return 'Brand Details';
      default: return 'Brand';
    }
  };

  const isReadOnly = mode === 'view';

  const inputStyle = { borderRadius: '6px', fontSize: '13px', height: '32px' };
  const labelStyle = { fontSize: '11px' };
  const helperStyle = { fontSize: '9px', margin: '1px 0 0 0' };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          style: {
            borderRadius: isMobile ? '0' : '12px',
            background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
            padding: '2px',
            margin: isMobile ? '0' : '8px'
          }
        }}
      >
        <Box style={{
          background: 'white',
          borderRadius: isMobile ? '0' : '10px',
          overflow: 'hidden',
          height: isMobile ? '100vh' : 'auto'
        }}>
          <DialogTitle style={{
            background: 'linear-gradient(135deg, #1d2445ff 0%, #1b0b2bff 100%)',
            color: 'white',
            padding: '8px 12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 'auto'
          }}>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <LocalOfferIcon style={{ fontSize: '18px' }} />
              <Typography variant="h6" style={{ fontWeight: '600', fontSize: '14px' }}>
                {getTitle()}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              style={{ color: 'white', padding: '2px' }}
              size="small"
            >
              <CloseIcon style={{ fontSize: '18px' }} />
            </IconButton>
          </DialogTitle>

          <DialogContent style={{
            padding: '8px 12px',
            paddingBottom: '4px'
          }}>
            <Typography variant="subtitle2" style={{
              color: '#666',
              marginBottom: '6px',
              fontSize: '10px',
              lineHeight: '1.2'
            }}>
              {mode === 'create' ? 'Add a new brand to your system' :
                mode === 'edit' ? 'Update brand information' :
                  'View brand details'}
            </Typography>

            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" style={{
                  fontWeight: '600',
                  marginBottom: '3px',
                  color: '#2d3748',
                  fontSize: '12px'
                }}>
                  Brand Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  name="Name"
                  label="Brand Name *"
                  value={formData.Name}
                  onChange={handleChange}
                  fullWidth
                  size="small"
                  error={!!errors.Name}
                  helperText={errors.Name}
                  disabled={isReadOnly}
                  placeholder="Enter brand name"
                  inputProps={{ maxLength: 150 }}
                  InputProps={{ style: inputStyle }}
                  InputLabelProps={{ style: labelStyle }}
                  FormHelperTextProps={{ style: helperStyle }}
                />
              </Grid>

            </Grid>
          </DialogContent>

          <DialogActions style={{
            padding: '8px 12px',
            background: '#f8f9fa',
            borderTop: '1px solid #e9ecef',
            gap: '6px',
            minHeight: 'auto'
          }}>
            <Button
              onClick={onClose}
              style={{
                color: '#6c757d',
                textTransform: 'none',
                fontWeight: '500',
                fontSize: '12px',
                padding: '4px 8px'
              }}
            >
              Cancel
            </Button>
            {!isReadOnly && (
              <Button
                onClick={handleSave}
                disabled={loading}
                variant="contained"
                style={{
                  background: 'linear-gradient(135deg, #53dac3ff 0%, #45a359ff 100%)',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: '600',
                  borderRadius: '4px',
                  padding: '4px 12px',
                  fontSize: '12px'
                }}
              >
                {loading ? 'Saving...' : mode === 'create' ? 'Add Brand' : 'Update Brand'}
              </Button>
            )}
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogContent style={{ textAlign: 'center', padding: '24px' }}>
          <WarningAmberIcon
            style={{
              fontSize: '64px',
              color: '#ff9800',
              marginBottom: '16px'
            }}
          />
          <Typography
            variant="h6"
            style={{
              fontWeight: '600',
              marginBottom: '12px',
              color: '#2d3748',
              fontSize: '16px'
            }}
          >
            Duplicate Brand Found
          </Typography>
          <Typography
            style={{
              color: '#666',
              fontSize: '14px',
              lineHeight: '1.5'
            }}
          >
            {duplicateMessage}
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '0 24px 24px 24px', justifyContent: 'center' }}>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            variant="contained"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              textTransform: 'none',
              fontWeight: '600',
              borderRadius: '8px',
              padding: '8px 32px',
              fontSize: '14px'
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BrandModal;

